import cx from 'clsx';
import { Spinner } from '@vechaiui/react';
import { useLocalization } from '@fluent/react';
import { useQuery } from '@apollo/client';

import {
  Customer,
  GetCustomerDocument,
  GetLogisticDocument,
  Logistics,
} from '#/__generated__/graphql/graphql';
import { formatAddress, formatUnitCode } from '#/utils/formatter';

const UserDetailsPanel: React.FC<UserDetailsPanelProps> = ({ userId, userType }) => {
  const { l10n } = useLocalization();
  const { loading, error, data } = useQuery(
    userType === 'customer' ? GetCustomerDocument : GetLogisticDocument,
    {
      variables: { id: userId },
    },
  );

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner size="xl" />
      </div>
    );
  }

  const user = data?.node as User;

  return (
    <div>
      {!loading && !!user && (
        <div className="space-y-5 text-black border-t border-neautral-500">
          <div className="mt-5 space-y-5">
            <DisplayPanel title={l10n.getString('name')}>
              <div>{`${user.name} (${user.companyNumber})`}</div>
            </DisplayPanel>
            {!!user.telephone && (
              <DisplayPanel title={l10n.getString('organization-telephone')}>
                <div>{user.telephone}</div>
              </DisplayPanel>
            )}
            {user.areasServed.length > 0 && (
              <div>
                <DisplayPanel title={l10n.getString('areas-served')}>
                  <div className="flex flex-wrap gap-3">
                    {user.areasServed.map((area) => (
                      <div className="px-3 py-2 border rounded-full border-neutral-400 w-fit">
                        {formatAddress(area.address, {
                          exclude: [
                            'addressCountry',
                            'addressLocality',
                            'streetAddress',
                            'postalCode',
                          ],
                        })}
                      </div>
                    ))}
                  </div>
                </DisplayPanel>
              </div>
            )}
          </div>

          {userType === 'logistic' && !!user.offers && user.offers.length > 0 && (
            <div>
              <DisplayPanel title={l10n.getString('offers')}>
                <div className="px-4 py-2 space-y-3 border rounded-xl border-neutral-300">
                  {user.offers.map((offer, index) => (
                    <div
                      key={offer.id}
                      className={cx(
                        index > 0 && 'border-t border-neutral-300',
                        'pt-4 pb-2 grid grid-cols-4 gap-3',
                      )}
                    >
                      <DisplayPanel title={l10n.getString('offer')}>
                        <p>{offer.itemOffered.name}</p>
                      </DisplayPanel>
                      <div className="max-w-max">
                        <DisplayPanel title={l10n.getString('offer-item-offered')}>
                          <p className="text-center">{offer.itemOffered.bodyType ?? '-'}</p>
                        </DisplayPanel>
                      </div>
                      <div className="max-w-max">
                        <DisplayPanel title={l10n.getString('truck-type')}>
                          {!!offer.itemOffered.cargoVolume ? (
                            <p className="text-center">
                              {offer.itemOffered.cargoVolume.value}{' '}
                              {formatUnitCode(offer.itemOffered.cargoVolume.unitCode ?? '')}
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </DisplayPanel>
                      </div>
                      <div className="max-w-max">
                        <DisplayPanel title={l10n.getString('offer-inventory-level')}>
                          <p className="text-center">{offer.inventoryLevel}</p>
                        </DisplayPanel>
                      </div>
                    </div>
                  ))}
                </div>
              </DisplayPanel>
            </div>
          )}

          {user.contactPoints.length > 0 && (
            <div>
              <DisplayPanel title={l10n.getString('contact-person')}>
                <div className="px-4 pt-1 pb-2 space-y-3 border rounded-xl border-neutral-300">
                  {user.contactPoints.map((contact, index) => (
                    <div
                      key={contact.name ?? '' + contact.email ?? '' + contact.telephone ?? ''} // At this point, I don't even know what I'm doing.
                      className={cx(
                        index > 0 && 'border-t border-neutral-300',
                        'pt-4 pb-2 grid grid-cols-3',
                      )}
                    >
                      {!!contact.name && (
                        <div className="w-11/12">
                          <DisplayPanel title={l10n.getString('name')}>
                            <div>{contact.name}</div>
                          </DisplayPanel>
                        </div>
                      )}
                      {!!contact.telephone && (
                        <DisplayPanel title={l10n.getString('telephone')}>
                          <div>{contact.telephone}</div>
                        </DisplayPanel>
                      )}
                      {!!contact.email && (
                        <DisplayPanel title={l10n.getString('email')}>
                          <div>{contact.email}</div>
                        </DisplayPanel>
                      )}
                    </div>
                  ))}
                </div>
              </DisplayPanel>
            </div>
          )}

          {!!user.referral && (
            <div className="pt-5 border-t border-neutral-300">
              <DisplayPanel title={l10n.getString('referral')}>
                <div>{user.referral}</div>
              </DisplayPanel>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export type UserDetailsPanelProps = {
  userId: string;
  userType: 'customer' | 'logistic';
};

type UserType = 'customer' | 'logistic';

type User = UserType extends 'customer' ? Customer : Logistics;

const DisplayPanel: React.FC<DisplayPanelProps> = ({ children, title }) => {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-primary-500">{title}</p>
      {children}
    </div>
  );
};

type DisplayPanelProps = {
  children: React.ReactNode;
  title: string;
};

export default UserDetailsPanel;
